import React, { useState, useEffect } from 'react';
import { Building2, Plus, Trash2, Edit3, MapPin, Loader2, CheckCircle, XCircle, Crown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { normalizeAppError } from '../../utils/apiErrors';
import { API } from '../../config/apiBase';


export default function EmpresaUnidadesPage() {
    const [unidades, setUnidades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ name: '', endereco: '' });
    const [saving, setSaving] = useState(false);
    const [isEnterprise, setIsEnterprise] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchUnidades = async () => {
        setLoading(true);
        try {
            const r = await fetch(`${API}/ponto/unidades`, { headers });
            if (r.ok) {
                const data = await r.json();
                setUnidades(Array.isArray(data) ? data : []);
                setIsEnterprise(true);
            } else if (r.status === 403) {
                setIsEnterprise(false);
            }
        } catch { setUnidades([]); }
        setLoading(false);
    };

    useEffect(() => { fetchUnidades(); }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error('Nome da unidade é obrigatório'); return; }
        setSaving(true);
        try {
            if (editingId) {
                const r = await fetch(`${API}/ponto/unidades/${editingId}`, {
                    method: 'PUT', headers, body: JSON.stringify({ name: form.name, endereco: form.endereco })
                });
                if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Erro'); }
                toast.success('Unidade atualizada!');
            } else {
                const r = await fetch(`${API}/ponto/unidades`, {
                    method: 'POST', headers, body: JSON.stringify({ name: form.name, endereco: form.endereco })
                });
                if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Erro'); }
                toast.success('Unidade criada!');
            }
            resetForm();
            fetchUnidades();
        } catch (err) { toast.error(normalizeAppError(err).message); }
        setSaving(false);
    };

    const handleEdit = (u) => {
        setEditingId(u.unidade_id);
        setForm({ name: u.name, endereco: u.endereco || '' });
        setShowForm(true);
    };

    const handleDelete = async (u) => {
        if (u.is_matriz) { toast.error('Não é possível excluir a unidade Matriz'); return; }
        if (!window.confirm(`Excluir a unidade "${u.name}"? Colaboradores vinculados ficarão sem unidade.`)) return;
        try {
            const r = await fetch(`${API}/ponto/unidades/${u.unidade_id}`, { method: 'DELETE', headers });
            if (!r.ok) { const d = await r.json(); throw new Error(d.detail || 'Erro'); }
            toast.success('Unidade excluída');
            fetchUnidades();
        } catch (err) { toast.error(normalizeAppError(err).message); }
    };

    const resetForm = () => { setShowForm(false); setEditingId(null); setForm({ name: '', endereco: '' }); };

    const matrizCount = unidades.filter(u => u.is_matriz).length;
    const filiaisCount = unidades.filter(u => !u.is_matriz).length;
    const activeCount = unidades.filter(u => u.status === 'active').length;

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    if (!isEnterprise) {
        return (
            <div className="text-center py-16 space-y-4" data-testid="unidades-upgrade">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)' }}>
                    <Crown size={28} style={{ color: 'var(--sv-gold-primary)' }} />
                </div>
                <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Múltiplas Unidades</h3>
                <p className="text-xs max-w-sm mx-auto" style={{ color: 'var(--muted)' }}>
                    Gerencie filiais, unidades e pontos de operação da sua empresa. Disponível no plano Enterprise.
                </p>
                <a href="/sistema/empresa/assinatura" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:scale-105"
                    style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="upgrade-enterprise-btn">
                    <Crown size={14} /> Ver plano Enterprise
                </a>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="empresa-unidades">
            {/* KPIs */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>{unidades.length}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Total</p>
                </div>
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: '#3B82F6' }}>{matrizCount}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Matriz</p>
                </div>
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: 'var(--sv-gold-primary)' }}>{filiaisCount}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Filiais</p>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between">
                <p className="text-xs" style={{ color: 'var(--muted)' }}>{activeCount} unidade(s) ativa(s)</p>
                <Button onClick={() => { resetForm(); setShowForm(true); }} className="btn-gold text-sm" data-testid="add-unidade-btn">
                    <Plus size={14} className="mr-1" /> Nova Unidade
                </Button>
            </div>

            {/* Form */}
            {showForm && (
                <form onSubmit={handleSave} className="rounded-xl p-4 sm:p-5 space-y-3" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.3)' }} data-testid="unidade-form">
                    <h4 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                        {editingId ? 'Editar Unidade' : 'Nova Unidade / Filial'}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input placeholder="Nome da unidade (ex: Filial Centro)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                            className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-unidade-name" />
                        <input placeholder="Endereço (opcional)" value={form.endereco} onChange={e => setForm({ ...form, endereco: e.target.value })}
                            className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-unidade-endereco" />
                    </div>
                    <div className="flex gap-2">
                        <Button type="submit" disabled={saving} className="btn-gold text-sm" data-testid="submit-unidade">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : editingId ? 'Salvar' : 'Criar Unidade'}
                        </Button>
                        <Button type="button" variant="outline" onClick={resetForm} className="text-sm">Cancelar</Button>
                    </div>
                </form>
            )}

            {/* List */}
            {unidades.length === 0 ? (
                <div className="text-center py-14 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <Building2 size={24} style={{ color: 'var(--muted)', opacity: 0.5 }} className="mx-auto mb-3" />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhuma unidade cadastrada</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Crie sua primeira unidade para organizar filiais e colaboradores.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {unidades.map((u) => (
                        <div key={u.unidade_id} className="flex items-center gap-3 p-3.5 rounded-xl transition-all hover:scale-[1.005]"
                            style={{ background: 'var(--surface)', border: `1px solid ${u.is_matriz ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}` }}
                            data-testid={`unidade-${u.unidade_id}`}>
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold shrink-0"
                                style={{ background: u.is_matriz ? 'rgba(45,212,191,0.12)' : 'rgba(59,130,246,0.12)', color: u.is_matriz ? '#2DD4BF' : '#3B82F6' }}>
                                <Building2 size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{u.name}</p>
                                    {u.is_matriz && (
                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full shrink-0" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)', color: 'var(--sv-accent)' }}>Matriz</span>
                                    )}
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full shrink-0"
                                        style={{ background: u.status === 'active' ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)', color: u.status === 'active' ? '#10B981' : '#6B7280' }}>
                                        {u.status === 'active' ? 'Ativa' : 'Inativa'}
                                    </span>
                                </div>
                                {u.endereco && <p className="text-xs truncate flex items-center gap-1 mt-0.5" style={{ color: 'var(--muted)' }}><MapPin size={10} /> {u.endereco}</p>}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => handleEdit(u)} className="p-2 rounded-lg transition-colors hover:bg-blue-500/10" data-testid={`edit-${u.unidade_id}`}>
                                    <Edit3 size={14} style={{ color: '#3B82F6' }} />
                                </button>
                                {!u.is_matriz && (
                                    <button onClick={() => handleDelete(u)} className="p-2 rounded-lg transition-colors hover:bg-red-500/10" data-testid={`delete-${u.unidade_id}`}>
                                        <Trash2 size={14} style={{ color: '#EF4444' }} />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Info */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
                <p className="text-xs font-semibold" style={{ color: 'var(--sv-gold-primary)' }}>Dica</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>
                    Vincule colaboradores a unidades na página de Colaboradores. Relatórios e comparativos por unidade estarão disponíveis em breve.
                </p>
            </div>
        </div>
    );
}
